/**
 * HLS AES-128 key deobfuscation for the browser.
 *
 * The server XOR-obfuscates encryption keys before sending them so that
 * standard HLS download tools (yt-dlp, Video DownloadHelper, etc.) receive
 * a mangled key and produce garbage output.  Our hls.js instances use a
 * custom loader (created by {@link createDeobfuscatingLoaderClass}) that
 * reverses the XOR transparently.
 *
 * The XOR pad is: HMAC-SHA256(HLS_OBFUSCATION_KEY, encodedPath)[0..15]
 * where HLS_OBFUSCATION_KEY is a derived key exposed via window.ENV.
 */
import type Hls from 'hls.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract the encodedPath segment from a /_/hls-key/{encodedPath}?sig=… URL. */
function extractEncodedPath(url: string): string | null {
    const match = url.match(/\/_\/hls-key\/([^?]+)/);
    return match ? decodeURIComponent(match[1]!) : null;
}

/** Derive the 16-byte XOR pad via HMAC-SHA256 (Web Crypto). */
async function deriveXorPad(encodedPath: string, obfuscationKey: string): Promise<Uint8Array> {
    const enc = new TextEncoder();
    const cryptoKey = await crypto.subtle.importKey(
        'raw',
        enc.encode(obfuscationKey),
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        [ 'sign' ]
    );
    const sig = await crypto.subtle.sign('HMAC', cryptoKey, enc.encode(encodedPath));
    return new Uint8Array(sig).slice(0, 16);
}

/** XOR data with a (cycled) pad. */
function xorBytes(data: Uint8Array, pad: Uint8Array): Uint8Array {
    const out = new Uint8Array(data.length);
    for (let i = 0; i < data.length; i++) {
        out[i] = data[i]! ^ pad[i % pad.length]!;
    }
    return out;
}

// ---------------------------------------------------------------------------
// Core deobfuscation
// ---------------------------------------------------------------------------

/**
 * Deobfuscate an HLS key response.
 *
 * @param keyUrl         Full /_/hls-key/… URL (used to extract the encoded path)
 * @param obfuscatedKey  Raw bytes returned by the server
 * @param obfuscationKey The HLS_OBFUSCATION_KEY string (from window.ENV)
 * @returns              The real 16-byte AES key
 */
export async function deobfuscateHlsKey(
    keyUrl: string,
    obfuscatedKey: ArrayBuffer,
    obfuscationKey: string,
): Promise<ArrayBuffer> {
    const encodedPath = extractEncodedPath(keyUrl);
    if (!encodedPath) {
        return obfuscatedKey;
    }
    const pad = await deriveXorPad(encodedPath, obfuscationKey);
    const plain = xorBytes(new Uint8Array(obfuscatedKey), pad);
    return plain.buffer as ArrayBuffer;
}

// ---------------------------------------------------------------------------
// hls.js custom loader
// ---------------------------------------------------------------------------

/**
 * Read the obfuscation key that the server injected via PublicEnv.
 * Returns `undefined` when the key isn't available (e.g. local dev without
 * the env var set) — callers should fall back to the default loader.
 */
export function getHlsObfuscationKey(): string | undefined {
    if ((window as any).ENV?.NODE_ENV === 'development') {
        return undefined;
    }
    return (window as any).ENV?.HLS_OBFUSCATION_KEY as string | undefined;
}

type LoaderClass = { new(config: any): any };

/**
 * Build an hls.js `loader` class that transparently deobfuscates AES-128 key
 * responses while delegating every other request to the default XHR loader.
 *
 * Usage:
 * ```ts
 * const hls = new Hls(buildHlsConfig());
 * ```
 */
export function createDeobfuscatingLoaderClass(DefaultLoader: LoaderClass, obfuscationKey: string): LoaderClass {
    return class DeobfuscatingLoader {
        private inner: InstanceType<LoaderClass>;

        constructor(config: any) {
            this.inner = new DefaultLoader(config);
        }

        // hls.js reads loader.stats externally (e.g. ABR abandon-rules check
        // accesses loader.stats.loading). We must proxy these properties from
        // the underlying XHR loader so the rest of the pipeline works.
        get stats() {
            return this.inner.stats;
        }

        get context() {
            return this.inner.context;
        }

        load(context: any, config: any, callbacks: any): void {
            const url: string = context.url ?? '';

            if (url.includes('/_/hls-key/')) {
                void this.loadDeobfuscatedKey(url, context, config, callbacks);
            } else {
                this.inner.load(context, config, callbacks);
            }
        }

        private async loadDeobfuscatedKey(url: string, context: any, config: any, callbacks: any): Promise<void> {
            // Let the inner loader handle the actual HTTP request so its stats
            // tracking stays consistent, then post-process the key bytes.
            const wrappedCallbacks = {
                onSuccess: async (response: any, stats: any, ctx: any, networkDetails: any) => {
                    try {
                        const keyData = await deobfuscateHlsKey(url, response.data, obfuscationKey);
                        callbacks.onSuccess({ ...response, data: keyData }, stats, ctx, networkDetails);
                    } catch (err: any) {
                        callbacks.onError(
                            { code: 0, text: err?.message ?? 'Key deobfuscation failed' },
                            context,
                            networkDetails,
                            stats
                        );
                    }
                },
                onError: callbacks.onError,
                onTimeout: callbacks.onTimeout,
                onAbort: callbacks.onAbort,
                onProgress: callbacks.onProgress
            };
            this.inner.load(context, config, wrappedCallbacks);
        }

        abort(): void {
            this.inner.abort();
        }

        destroy(): void {
            this.inner.destroy();
        }
    };
}

/**
 * Build a complete hls.js config object with credentials + key deobfuscation.
 * If the obfuscation key is not available, returns config without the custom
 * loader (keys will be fetched normally — playback will fail if the server
 * actually obfuscates them).
 */
export function buildHlsConfig(HlsClass: typeof Hls): ConstructorParameters<typeof Hls>[0] {
    const cfg: ConstructorParameters<typeof Hls>[0] = {
        xhrSetup: (xhr: XMLHttpRequest) => {
            xhr.withCredentials = true;
        }
    };

    const key = getHlsObfuscationKey();
    if (key) {
        cfg.loader = createDeobfuscatingLoaderClass(
            HlsClass.DefaultConfig.loader as unknown as LoaderClass,
            key
        );
    }

    return cfg;
}
